import React, { useEffect, useRef, useState } from "react";
import { FaFileExcel } from "react-icons/fa";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { parseBranchKey } from "../../functions/mdmFolderUtils";

const MdmFullyFleetControls = ({
  displayedDevices,
  exportCurrentViewToExcel,
  openCommandModal,
  openFieldCommandModal,
  showFieldCommandButton,
  onRestartCache,
  isRestartingCache,
  moveTarget,
  setMoveTarget,
  isMovingDevices,
  retailerNames,
  allDevices,
  folderOptions,
  selectedDevicesHomeBranchKey,
  onMoveSelected,
  selectedDeviceIds,
  triggeredSwanSongDeviceIds = [],
  onShowSwanSongResults,
  triggeredCameraDevices = [],
  onShowCameraResults,
  showLocationVersionButton = false,
  onOpenLocationVersionWindow,
  canEditDeviceNotes = false,
  onEditSelectedNotes,
  isSavingBulkNotes = false,
  showFieldStatusIndicators = false,
  fieldStatusCounts = null,
  selectedFieldJourneyStatus = null,
  onFieldJourneyStatusFilterToggle,
  onRefreshFieldStatus,
  isRefreshingFieldStatus = false,
}) => {
  const [isOperationalMenuOpen, setIsOperationalMenuOpen] = useState(false);
  const operationalMenuRef = useRef(null);

  useEffect(() => {
    if (!isOperationalMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!operationalMenuRef.current?.contains(event.target)) {
        setIsOperationalMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOperationalMenuOpen]);

  return (
    <div className="p-4 border-b border-outline-variant/5 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <h3 className="font-headline font-bold text-on-surface">Device Fleet</h3>
        <span className="bg-primary-fixed text-on-primary-fixed px-2 py-0.5 rounded text-[10px] font-bold">
          LIVE DATA
        </span>
        {showFieldStatusIndicators && (
          <div className="flex items-center gap-3 ml-1 pl-3 border-l border-outline-variant/20">
            <button
              type="button"
              className={`flex items-center gap-1 text-xs text-on-surface-variant rounded-md px-1.5 py-0.5 transition-colors ${selectedFieldJourneyStatus === "active"
                  ? "bg-primary/10 ring-1 ring-primary/30"
                  : "hover:bg-surface-container-high"
                }`}
              onClick={() => onFieldJourneyStatusFilterToggle?.("active")}
              disabled={!onFieldJourneyStatusFilterToggle}
              aria-pressed={selectedFieldJourneyStatus === "active"}
              title="Show active devices only"
            >
              <span className="font-semibold text-green-600 tabular-nums">
                {fieldStatusCounts?.active ?? "—"}
              </span>
              <ShoppingCartCheckoutIcon sx={{ color: "green", fontSize: 20 }} />
              <span>Active</span>
            </button>
            <button
              type="button"
              className={`flex items-center gap-1 text-xs text-on-surface-variant rounded-md px-1.5 py-0.5 transition-colors ${selectedFieldJourneyStatus === "idle"
                  ? "bg-primary/10 ring-1 ring-primary/30"
                  : "hover:bg-surface-container-high"
                }`}
              onClick={() => onFieldJourneyStatusFilterToggle?.("idle")}
              disabled={!onFieldJourneyStatusFilterToggle}
              aria-pressed={selectedFieldJourneyStatus === "idle"}
              title="Show idle devices only"
            >
              <span className="font-semibold text-[#5b5858] tabular-nums">
                {fieldStatusCounts?.idle ?? "—"}
              </span>
              <ShoppingCartIcon sx={{ color: "#5b5858", fontSize: 20 }} />
              <span>Idle</span>
            </button>
            <button
              type="button"
              className={`flex items-center gap-1 text-xs text-on-surface-variant rounded-md px-1.5 py-0.5 transition-colors ${selectedFieldJourneyStatus === "offline"
                  ? "bg-primary/10 ring-1 ring-primary/30"
                  : "hover:bg-surface-container-high"
                }`}
              onClick={() => onFieldJourneyStatusFilterToggle?.("offline")}
              disabled={!onFieldJourneyStatusFilterToggle}
              aria-pressed={selectedFieldJourneyStatus === "offline"}
              title="Show offline devices only"
            >
              <span className="font-semibold text-[#ff3636] tabular-nums">
                {fieldStatusCounts?.offline ?? "—"}
              </span>
              <ShoppingCartIcon sx={{ color: "#ff3636", fontSize: 20 }} />
              <span>Offline</span>
            </button>
            <button
              className="px-3 py-1.5 bg-surface-container-high text-on-surface font-semibold text-xs rounded-lg hover:bg-surface-container-highest transition-colors disabled:opacity-60"
              onClick={onRefreshFieldStatus}
              disabled={isRefreshingFieldStatus}
              type="button"
            >
              {isRefreshingFieldStatus ? "Loading..." : "Load Status"}
            </button>
          </div>
        )}
      </div>
      <div className="flex space-x-2 items-center">
        <button
          className="px-4 py-2 bg-gradient-to-br from-primary to-primary-container text-white font-semibold text-xs rounded-lg shadow-sm hover:shadow-primary/20 transition-all flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={openCommandModal}
          disabled={selectedDeviceIds.length === 0}
          type="button"
        >
          <span className="material-symbols-outlined text-sm mr-2">terminal</span>
          MDM Commands
        </button>
        {showFieldCommandButton && (
          <button
            className="px-4 py-2 bg-gradient-to-br from-primary to-primary-container text-white font-semibold text-xs rounded-lg shadow-sm hover:shadow-primary/20 transition-all flex items-center disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={openFieldCommandModal}
            disabled={selectedDeviceIds.length === 0}
            type="button"
          >
            <span className="material-symbols-outlined text-sm mr-2">terminal</span>
            Field Commands
          </button>
        )}

        <div className="relative" ref={operationalMenuRef}>
          <button
            className="px-4 py-2 bg-surface-container-high text-on-surface font-semibold text-xs rounded-lg hover:bg-surface-container-highest transition-colors flex items-center"
            onClick={() => setIsOperationalMenuOpen((prev) => !prev)}
            type="button"
          >
            <span className="material-symbols-outlined text-sm mr-2">build</span>
            Operational Commands
          </button>
          {isOperationalMenuOpen && (
            <div className="absolute right-0 mt-2 w-[360px] rounded-xl border border-outline-variant/20 bg-surface shadow-lg p-3 z-20">
              <p className="text-xs font-semibold text-on-surface mb-2">Operational Commands</p>
              <button
                className="w-full px-4 py-2 bg-surface-container-high text-on-surface font-semibold text-xs rounded-lg hover:bg-surface-container-highest transition-colors disabled:opacity-60"
                onClick={onRestartCache}
                disabled={isRestartingCache}
                type="button"
              >
                {isRestartingCache ? "Restarting Cache..." : "Restart Cache"}
              </button>
              {canEditDeviceNotes && (
                <button
                  className="mt-2 w-full px-4 py-2 bg-surface-container-high text-on-surface font-semibold text-xs rounded-lg hover:bg-surface-container-highest transition-colors disabled:opacity-60"
                  onClick={() => {
                    setIsOperationalMenuOpen(false);
                    onEditSelectedNotes?.();
                  }}
                  disabled={isSavingBulkNotes || selectedDeviceIds.length === 0}
                  type="button"
                >
                  {isSavingBulkNotes
                    ? "Saving Notes..."
                    : `Edit Notes (${selectedDeviceIds.length})`}
                </button>
              )}
              <p className="text-xs font-semibold text-on-surface mt-3 mb-2">Move Devices</p>
              <select
                className="w-full px-3 py-2 bg-surface-container-high text-on-surface font-semibold text-xs rounded-lg border-none outline-none focus:ring-2 focus:ring-primary/20"
                value={moveTarget}
                onChange={(e) => setMoveTarget(e.target.value)}
                disabled={isMovingDevices}
              >
                <option value="">Move to…</option>
                {selectedDevicesHomeBranchKey && (() => {
                  const { retailerName, branchName } = parseBranchKey(selectedDevicesHomeBranchKey);
                  return (
                    <optgroup label="All Devices (home branch)">
                      <option
                        key={`branch::${selectedDevicesHomeBranchKey}`}
                        value={`branch::${selectedDevicesHomeBranchKey}`}
                      >
                        {retailerName} / {branchName} / All Devices
                      </option>
                    </optgroup>
                  );
                })()}
                <optgroup label="Folders">
                  {folderOptions.map((folder) => (
                    <option key={folder.id} value={`folder::${folder.id}`}>
                      {folder.label}
                    </option>
                  ))}
                </optgroup>
              </select>
              <button
                className="mt-2 w-full px-4 py-2 bg-surface-container-high text-on-surface font-semibold text-xs rounded-lg hover:bg-surface-container-highest transition-colors disabled:opacity-60"
                onClick={onMoveSelected}
                disabled={
                  isMovingDevices ||
                  selectedDeviceIds.length === 0 ||
                  !moveTarget
                }
                type="button"
              >
                {isMovingDevices ? "Moving..." : `Move Selected (${selectedDeviceIds.length})`}
              </button>
            </div>
          )}
        </div>
        <button
          className="h-9 w-9 bg-surface-container-high text-on-surface rounded-lg hover:bg-surface-container-highest transition-colors inline-flex items-center justify-center disabled:opacity-60"
          onClick={exportCurrentViewToExcel}
          disabled={displayedDevices.length === 0}
          type="button"
          title="Export Excel"
          aria-label="Export Excel"
        >
          <FaFileExcel className="text-green-600 text-lg" />
        </button>
        {showLocationVersionButton && (
          <button
            className="h-9 w-9 bg-surface-container-high text-on-surface rounded-lg hover:bg-surface-container-highest transition-colors inline-flex items-center justify-center disabled:opacity-60"
            onClick={() => onOpenLocationVersionWindow?.()}
            disabled={selectedDeviceIds.length === 0}
            type="button"
          >
            <span className="material-symbols-outlined text-sm">location_on</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default MdmFullyFleetControls;
